import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, Subject, Subscription, map, catchError, throwError } from 'rxjs';
import { ApplyAssignmentRequest, AssignmentRequest, AssignmentResponse, GetAssignmentsResponse, GetPaginatedAlgorithmicExecutionsResponse, PaginatedAssignmentsRequest } from 'src/app/shared/assigner-table/models';
import { AssignerEventType, EventCallback } from 'src/app/shared/assigner-table/types';
import { environment } from 'src/environments/environment';



@Injectable({
  providedIn: 'root',
})
export class AssignerService {
  private readonly apiUrl = environment.url;
  private readonly channels = new Map<AssignerEventType, Subject<unknown>>();

  constructor(private readonly http: HttpClient) {}

  /**
   * Subscribe to assignment events
   * @param topic - Event type to subscribe to
   * @param observer - Callback function to handle event data
   * @returns Subscription object for cleanup
   */
  subscribe<T = unknown>(
    topic: AssignerEventType,
    observer: EventCallback<T>
  ): Subscription {
    if (!this.channels.has(topic)) {
      this.channels.set(topic, new Subject<unknown>());
    }

    const subject = this.channels.get(topic)!;
    return subject.subscribe(observer as EventCallback<unknown>);
  }

  /**
   * Publish event data to subscribers
   * @param topic - Event type to publish
   * @param data - Event data to send
   */
  publish<T = unknown>(topic: AssignerEventType, data: T): void {
    const subject = this.channels.get(topic);
    if (!subject) {
      console.warn(`No subscribers for topic: ${topic}`);
      return;
    }

    subject.next(data);
  }

  /**
   * Save assignment data (legacy method - consider deprecation)
   * @param data - Assignment data to save
   * @returns Observable of the saved data
   * @deprecated Use addAssignment instead
   */
  saveAssignment<T>(data: T): Observable<T> {
    return from([data]);
  }

  /**
   * Retrieve all assignment executions
   * @returns Observable containing assignment execution models
   */
  getAssignments(): Observable<GetAssignmentsResponse> {
    return this.http.get<GetAssignmentsResponse>(`${this.apiUrl}/algorithms`);
  }

  /**
   * Create a new assignment
   * @param assignment - Assignment data to create
   * @returns Observable containing the assignment response
   */
  addAssignment(assignment: AssignmentRequest): Observable<AssignmentResponse> {
    return this.http.post<AssignmentResponse>(
      `${this.apiUrl}/algorithms/add`,
      assignment
    );
  }

  /**
   * Apply an existing assignment execution
   * @param request - Apply assignment request with execution ID
   * @returns Observable containing the apply response
   */
  applyAssignment(
    request: ApplyAssignmentRequest
  ): Observable<AssignmentResponse> {
    return this.http.post<AssignmentResponse>(
      `${this.apiUrl}/algorithms/apply`,
      request
    );
  }

  /**
   * Clean up all event subscriptions
   * Should be called when service is destroyed (rarely needed for singleton services)
   */
  destroy(): void {
    this.channels.forEach((subject) => subject.complete());
    this.channels.clear();
  }

  getAssignmentsPaginated(
    request: PaginatedAssignmentsRequest
  ): Observable<GetPaginatedAlgorithmicExecutionsResponse> {
    const pageNumber = Math.max(1, request.pageIndex + 1);

    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(request.pageSize));

    if (request.zoneGuid) {
      params = params.set('zoneGuid', request.zoneGuid);
    }

    return this.http
      .get<GetPaginatedAlgorithmicExecutionsResponse>(`${this.apiUrl}/algorithms/paginated`, { params })
      .pipe(
        map((resp) => ({
          executions: Array.isArray(resp.executions) ? resp.executions : [],
          totalItems: typeof resp.totalItems === 'number' ? resp.totalItems : 0,
          hasNextPage: Boolean(resp.hasNextPage),
          hasPreviousPage: Boolean(resp.hasPreviousPage),
        })),
        catchError((err) => {
          console.error('AssignerService.getAssignmentsPaginated error', err);
          return throwError(
            () => new Error('Failed to fetch paginated assignments')
          );
        })
      );
  }
}
